import { changeSinceLastMonth, formatKpi, sparklinePoints, trendSentence, type KpiUnit, type TrendPoint } from "../trend";
import type { AccountsView } from "../types";

const STATUS: Record<string, { glyph: string; word: string; cls: string }> = {
  good: { glyph: "●", word: "on target", cls: "rag rag--good" },
  warning: { glyph: "◐", word: "watch", cls: "rag rag--warning" },
  critical: { glyph: "▲", word: "concern", cls: "rag rag--critical" },
  none: { glyph: "–", word: "no target", cls: "rag" },
};

const money = (pounds: number) =>
  pounds >= 1_000_000 ? `£${(pounds / 1_000_000).toFixed(2)}m` : `£${Math.round(pounds / 1000).toLocaleString("en-GB")}k`;

const monthName = (period: string) =>
  new Date(`${period}-01T00:00:00Z`).toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });

const SPARK_W = 96;
const SPARK_H = 26;

/** A small trend line. Neutral ink, not RAG: the direction is described in words, not judged by colour. */
export function Sparkline({ trend, unit, label }: { trend: TrendPoint[]; unit: KpiUnit; label: string }) {
  const text = trendSentence(trend, unit);
  if (trend.length < 2) return <span className="spark-none">{text}</span>;
  const pts = sparklinePoints(trend.map((t) => t.value), SPARK_W, SPARK_H);
  const last = pts[pts.length - 1]!;
  return (
    <svg className="spark" width={SPARK_W} height={SPARK_H} viewBox={`0 0 ${SPARK_W} ${SPARK_H}`} role="img" aria-label={`${label}: ${text}`}>
      <title>{text}</title>
      <polyline points={pts.map((p) => `${p.x},${p.y}`).join(" ")} fill="none" stroke="currentColor" strokeWidth="1.5"
        strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last.x} cy={last.y} r="2.5" fill="currentColor" />
    </svg>
  );
}

/**
 * The latest management accounts the firm has actually presented (11 §8). Only the six MVP KPIs;
 * the numbers are clues, not a control panel. Status is shown with a glyph and a word, never colour alone.
 */
export function AccountsPanel({ accounts }: { accounts: AccountsView | null }) {
  return (
    <section className="panel" aria-labelledby="acc-title">
      <h2 id="acc-title" className="panel-title">Management accounts</h2>
      {!accounts ? (
        <>
          <p>No management accounts have been presented to you yet.</p>
          <p className="panel-note">
            The pack arrives at month end, with the partners' meeting. When it does, you will see the six figures the
            firm watches, how each has moved over recent months, and the change since the month before.
          </p>
        </>
      ) : (
        <>
          <p className="panel-note">
            {monthName(accounts.period)} · fees billed {money(accounts.feesBilled)} · cash at month end {money(accounts.cash)}
          </p>
          <div className="table-scroll" tabIndex={0} role="region" aria-labelledby="acc-caption">
            <table className="acc-table">
              <caption id="acc-caption" className="visually-hidden">Management accounts for {monthName(accounts.period)}</caption>
              <thead>
                <tr>
                  <th scope="col">Measure</th>
                  <th scope="col">Figure</th>
                  <th scope="col">Since last month</th>
                  <th scope="col">Recent months</th>
                  <th scope="col">Against target</th>
                </tr>
              </thead>
              <tbody>
                {accounts.kpis.map((k) => {
                  const s = STATUS[k.status] ?? STATUS.none!;
                  const change = changeSinceLastMonth(k.trend, k.unit);
                  return (
                    <tr key={k.key}>
                      <th scope="row">{k.label}</th>
                      <td className="num">{formatKpi(k.value, k.unit)}</td>
                      <td className="num">{change ?? <span className="muted">first month</span>}</td>
                      <td className="acc-trend"><Sparkline trend={k.trend} unit={k.unit} label={k.label} /></td>
                      <td><span className={s.cls}><span aria-hidden="true">{s.glyph}</span> {s.word}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="panel-note">Lines show each figure for the months presented to you, oldest on the left.</p>
        </>
      )}
    </section>
  );
}
